import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useNotifications } from "@/context/NotificationsContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Loading, Btn, EmptyState } from "@/components/primitives";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDate, monthOptions, statusMeta } from "@/lib/format";
import { Truck, Plus, Search } from "lucide-react";

export default function Transports() {
  const [calls, setCalls] = useState(null);
  const { subscribe } = useNotifications();
  const { user, effectiveIsAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(params.get("status") || "all");
  const [month, setMonth] = useState(params.get("month") || "all");
  const [q, setQ] = useState(params.get("q") || "");
  const navigate = useNavigate();

  useEffect(() => {
    setQ(params.get("q") || "");
  }, [params]);

  useEffect(() => {
    const buildUrl = () => {
      let url = "/calls";
      const qs = [];
      if (status !== "all") qs.push(`status=${status}`);
      if (month !== "all") qs.push(`month=${month}`);
      if (qs.length) url += "?" + qs.join("&");
      return url;
    };
    const refresh = (silent) => {
      if (!silent) setCalls(null);
      api.get(buildUrl()).then((r) => setCalls(r.data)).catch(() => setCalls([]));
    };
    refresh(false);
    const id = setInterval(() => refresh(true), 60000); // safety-net; WS handles real-time
    const onFocus = () => { if (document.visibilityState === "visible") refresh(true); };
    document.addEventListener("visibilitychange", onFocus);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onFocus); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, month]);

  // Real-time dispatch board via WebSocket (call.new / call.updated / call.deleted).
  useEffect(() => {
    const inFilter = (c) => (status === "all" || c.status === status) && (month === "all" || c.month === month);
    const off = subscribe((evt) => {
      if (evt.type === "call.new" || evt.type === "call.updated") {
        const c = evt.call;
        if (!c) return;
        setCalls((prev) => {
          if (!prev) return prev;
          const without = prev.filter((x) => x.id !== c.id);
          return inFilter(c) ? [c, ...without] : without;
        });
      } else if (evt.type === "call.deleted") {
        setCalls((prev) => prev ? prev.filter((x) => x.id !== evt.id) : prev);
      }
    });
    return off;
  }, [subscribe, status, month]);

  const filtered = useMemo(() => {
    if (!calls) return [];
    const term = q.trim().toLowerCase();
    let list = calls;
    // Driver view: only calls where the admin is the (primary/back-up) driver.
    if (!effectiveIsAdmin && user) {
      list = list.filter((c) => c.assigned_to === user.id || c.assigned_to_secondary === user.id || c.driver_id === user.id);
    }
    if (term) {
      list = list.filter((c) =>
        [c.deceased_name, c.agency_for, c.pickup_location, c.dropoff_location, c.assigned_to_name, c.driver_name]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(term))
      );
    }
    return [...list].sort((a, b) => new Date(b.scheduled_at || b.created_at) - new Date(a.scheduled_at || a.created_at));
  }, [calls, q, effectiveIsAdmin, user]);

  const sel = "h-9 px-3 rounded-md border border-curtron-line bg-curtron-surface text-sm outline-none focus:border-curtron-green transition-colors";
  const TABS = ["all", "dispatched", "accepted", "on_scene", "transporting", "delivered", "completed"];

  return (
    <div>
      <PageHeader title={effectiveIsAdmin ? "Transport Calls" : "My Calls"} subtitle={effectiveIsAdmin ? "Dispatch board — all transport calls." : "Transport calls assigned to you."}>
        {effectiveIsAdmin && <Btn onClick={() => navigate("/transports/new")} data-testid="new-transport-btn"><Plus className="w-4 h-4" /> New call</Btn>}
      </PageHeader>

      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 mb-3 -mx-1 px-1" data-testid="transports-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setStatus(t)} data-testid={`tab-${t}`}
            className={`shrink-0 h-8 px-3 rounded-full text-sm font-medium transition-colors border ${status === t ? "bg-curtron-green text-white border-curtron-green" : "bg-curtron-surface text-curtron-sub border-curtron-line hover:bg-curtron-bg"}`}>
            {t === "all" ? "All" : statusMeta(t).label}
          </button>
        ))}
      </div>

      <Card className="p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-curtron-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="transports-search"
            placeholder="Search name, agency, location…"
            className="w-full h-9 pl-9 pr-3 rounded-md border border-curtron-line bg-curtron-bg text-sm outline-none focus:border-curtron-green focus:bg-curtron-surface transition-colors" />
        </div>
        <select value={month} onChange={(e) => setMonth(e.target.value)} className={sel} data-testid="transports-month-filter">
          <option value="all">All months</option>
          {monthOptions().map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </Card>

      <Card className="overflow-hidden">
        {calls === null ? (
          <Loading />
        ) : filtered.length === 0 ? (
          <EmptyState icon={Truck} title="No transports found" subtitle={effectiveIsAdmin ? "Adjust filters or create a new transport." : "You have no calls assigned to you."}
            action={effectiveIsAdmin ? <Btn onClick={() => navigate("/transports/new")}><Plus className="w-4 h-4" /> New call</Btn> : null} />
        ) : (
          <div className="divide-y divide-curtron-line">
            {filtered.map((c) => (
              <button key={c.id} onClick={() => navigate(`/transports/${c.id}`)} data-testid={`transport-row-${c.id}`}
                className="w-full text-left px-4 py-3.5 hover:bg-curtron-bg transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-curtron-ink text-sm truncate">{c.agency_for || "—"}</div>
                    <div className="text-sm text-curtron-sub truncate">{c.deceased_name || "—"}</div>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
                <div className="text-xs text-curtron-muted mt-1.5 truncate">{c.pickup_location} → {c.dropoff_location}</div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-curtron-muted">{fmtDate(c.scheduled_at)}</span>
                  <span className="text-xs text-curtron-sub truncate max-w-[45%]">{c.assigned_to_name || c.driver_name || "Unassigned"}{c.van_number ? ` · ${c.van_number}` : ""}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>
      {calls && <p className="text-xs text-curtron-muted mt-3">{filtered.length} transport{filtered.length !== 1 ? "s" : ""}</p>}
    </div>
  );
}
